(ns rothko.editor.document.navigators
  (:require
   [clojure.string :as str]
   [com.rpl.specter :as sp]
   [com.rpl.specter.navs :as n]
   [medley.core :as medley]
   [rothko.editor.document.navigator :refer [selectionnav]]
   [rothko.editor.document.script :as script :refer [caret selection]]
   [rothko.editor.document.script.spec :as script-spec]
   [rothko.editor.matcher :as m]))

(sp/defnav
  ^{:doc "Like `srange`, but selects only the existing sub-range."}
  safe-srange
  [start end]
  (select*
   [_ structure next-fn]
   (let [length (count structure)
         start (max start 0)
         end (min end length)]
     (if (> start end)
       [] ; TODO: I think this should be (next-fn []).
       (n/srange-select structure start end next-fn))))
  (transform*
   [_ structure next-fn]
   (let [length (count structure)
         start (max start 0)
         end (min end length)]
     (n/srange-transform structure start end next-fn))))

(def
  ^{:doc "Navigates to a vector containing the selected tokens."}
  selection-srange
  (selectionnav
   []
   (select*
    [_ {:keys [start-index end-index] :as selection} structure next-fn]
    (next-fn
     (sp/select-one
      (sp/srange
       start-index
       (inc end-index))
      structure)))
   (transform*
    [_ {:keys [start-index end-index]} structure next-fn]
    (sp/transform
     (sp/srange
      start-index
      (inc end-index))
     #(next-fn %)
     structure))))

(def selected
  [(sp/collect-one :selection)
   :script
   selection-srange
   sp/DISPENSE])

(def extended-selection-srange
  (selectionnav
   [before after]
   (select*
    [_ {:keys [start-index end-index]} structure next-fn]
    (next-fn
     (sp/select-one
      (safe-srange
       (- start-index before)
       (inc (+ end-index after)))
      structure)))
   (transform*
    [_ {:keys [start-index end-index]} structure next-fn]
    (sp/transform
     (safe-srange
      (- start-index before)
      (inc (+ end-index after)))
     next-fn
     structure))))

(def
  ^{:doc "A selector returning a three-element vector:
          * `before` number of tokens preceding the current editor selection,
          * the token(s) currently selected in the editor,
          * `after` number of tokens following the current editor selection."}
  selection-srange-with-context
  (selectionnav
   [before after]
   (select*
    [_ {:keys [start-index start-offset end-index end-offset]} structure next-fn]
    (next-fn
     (sp/select
      (sp/multi-path
       (safe-srange
        (- start-index before)
        start-index)
       (safe-srange
        start-index
        (inc end-index))
       (safe-srange
        (inc end-index)
        (inc (+ end-index after))))
      structure)))))

(defn offset-to-last
  [offset]
  (sp/srange-dynamic (constantly offset) count))

(defn first-to-offset
  [offset]
  (sp/srange 0 offset))

(def
  ^{:doc "A selector returning a two-element vector of:
          * a string preceding the selected offset in the first selected token,
          * a string following the selected offset in the last selected token."}
  surrounds
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (select*
       [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
       (next-fn
        (sp/select
         [:script
          (sp/multi-path
           [(sp/nthpath start-index)
            :word
            (first-to-offset start-offset)]
           [(sp/nthpath end-index)
            :word
            (offset-to-last end-offset)])]
         structure))))]))

(def remove-selection
  ;; Shouldn't need to be a selectionnav anymore
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (transform*
       [_ {:keys [start-index end-index] :as selection} structure next-fn]
       (sp/transform
        [(sp/collect-one
          :selection)

         (sp/collect-one
          [surrounds
           sp/DISPENSE])

         (sp/collect-one
          [(sp/collect-one
            :selection)
           :script
           ;; TODO don't need extended range here anymore. or do we?
           ;; check deleting last space before word token. Maybe the
           ;; terminal needs to be multi-fn?
           (selection-srange-with-context 2 2)
           sp/DISPENSE])

         (sp/multi-path
          [:script
           (extended-selection-srange 2 2)
           (sp/terminal
            (fn [_
                 [start-word-fragment end-word-fragment]
                 [prev-tokens selected next-tokens]
                 _]
              (let [start-selected (first selected)
                    end-selected (last selected)
                    both-word-fragments (str start-word-fragment end-word-fragment)
                    tokens (->> (if (= (:type start-selected) (:type end-selected))
                                  [(assoc start-selected :word both-word-fragments)]
                                  [(assoc start-selected :word start-word-fragment)
                                   (assoc end-selected :word end-word-fragment)])
                                (filter #(seq (:word %))))]
                (->> [prev-tokens
                      tokens
                      next-tokens]
                     (sp/select
                      [sp/ALL
                       sp/ALL])))))]

          [:selection
           (sp/terminal
            (fn [_ [start-word-fragment end-word-fragment] context {:keys [start-index] :as selection}]
              (let [word (str start-word-fragment end-word-fragment)]
                (if (= 0 (count word))
                  (let [new-offset (-> context first last :word count)
                        ;; Guard against deleting the last char at index 0
                        new-index (max 0 (dec start-index))]
                    {:start-index new-index
                     :start-offset new-offset
                     :end-index new-index
                     :end-offset new-offset})
                  {:start-index start-index
                   :start-offset (count start-word-fragment)
                   :end-index start-index
                   :end-offset (count start-word-fragment)}))))])]
        next-fn
        structure)))]))

(defn caret?
  [{:keys [start-index start-offset end-index end-offset] :as selection}]
  (= [start-index start-offset]
     [end-index end-offset]))

(defn CARET?
  [{:keys [selection] :as document}]
  (assert (contains? document :selection)
          "`CARET?` navigator takes the whole `document` as arg")
  (caret? selection))

(defn to-next-start [[index _offset]]
  [(inc index) 0])

(defn start-bias [[index offset :as location] script]
  (let [last (dec (count script))]
    (if (> index last)
      [last (-> script (get (dec last)) :word count)]
      (let [this-len (-> script (get index) :word count)]
        (if (= offset this-len)
          (to-next-start location)
          location)))))

(defn to-prev-end [[index _offset :as location] script]
  (if (zero? index) location
      (let [prev-len (-> script (get (dec index)) :word count)]
        [(dec index) prev-len])))

(defn end-bias [[_index offset :as location] script]
  (if (zero? offset)
    (to-prev-end location script)
    location))

(def token-unbias
  (sp/transformed
   [(sp/collect-one :script)
    :selection]
   (fn [script {:keys [start-index start-offset] :as sel}]
     (if (caret? sel)
       (if (and (zero? start-offset) (pos? start-index))
         (let [new-idx (dec start-index)
               new-ofs (sp/select-one [(sp/nthpath new-idx) :word (sp/view count)] script)]
           (caret new-idx new-ofs))
         sel)
       sel))))

(def token-rebias
  [(sp/collect-one :script)
   :selection
   (sp/terminal
    (fn [& args]
      (let [[script {:keys [start-index start-offset end-index end-offset] :as sel}] (take-last 2 args)
            [adj-idx adj-ofs] (start-bias [start-index start-offset] script)
            [alt-idx alt-ofs] (end-bias [end-index end-offset] script)]
        (if (caret? sel)
          (let [prev (get script (dec adj-idx) {:type :space})
                this (get script adj-idx {:type :space})]
            (if (or (#{:space :space-edit} (:type this))
                    (#{:word :edit} (:type prev)))
              (caret alt-idx alt-ofs)
              (caret adj-idx adj-ofs)))
          (selection adj-idx adj-ofs alt-idx alt-ofs)))))])

(def merge-similar*
  (selectionnav
   [keep-system-flag?]
   (transform*
    [_ {:keys [start-index end-index start-offset] :as selection} structure next-fn]
    (assert (caret? selection) "can only merge with caret")
    (let [merge-type? (complement #{:word :space-edit})
          merge-two (fn [a b]
                      (cond-> {:word (str (:word a)
                                          (:word b))
                               :type (:type a)}
                        (and keep-system-flag?
                             (or (:system? a) (:system? b)))
                        (assoc :system? true)))
          merge-token-range (fn [[x y z :as token-range]]
                              (-> (cond
                                    (and (not (nil? y))
                                         (not (nil? z))
                                         (= (:type x)
                                            (:type y)
                                            (:type z))
                                         (merge-type? (:type x)))
                                    {:token-range [(-> (merge-two x y)
                                                       (merge-two z))]
                                     :new-selection (script/caret
                                                     (dec start-index)
                                                     (+ (count (:word x))
                                                        (count (:word y))))}

                                    (and (not (nil? y))
                                         (= (:type x)
                                            (:type y))
                                         (merge-type? (:type x)))
                                    {:token-range [(merge-two x y)
                                                   z]
                                     :new-selection (when (pos? start-index)
                                                      (script/caret
                                                       (dec start-index)
                                                       (+ (count (:word x))
                                                          start-offset)))}

                                    (and (not (nil? y))
                                         (= (:type y)
                                            (:type z))
                                         (merge-type? (:type y)))
                                    {:token-range [x
                                                   (merge-two y z)]}

                                    :else {:token-range token-range})
                                  (update :token-range #(remove nil? %))))
          extended-selection (extended-selection-srange 1 1)]
      (sp/transform
       (sp/multi-path
        [(sp/collect-one :selection)
         :script
         extended-selection
         (sp/terminal
          (fn [_ token-range]
            (-> token-range
                merge-token-range
                :token-range)))]
        [:selection
         (sp/terminal
          (fn [selection]
            (let [token-range (sp/select-one
                               (sp/with-fresh-collected
                                 (sp/collect-one :selection)
                                 :script
                                 extended-selection)
                               structure)
                  {:keys [new-selection]} (merge-token-range token-range)]
              (or new-selection
                  selection))))])
       next-fn
       structure)))))

(defn merge-similar
  "Merges adjacent tokens if they are of the same type and are not words
  or space-edits.
  Takes into account the caret token and one token on each side."
  ([] (merge-similar false))
  ([keep-system-flag?]
   (sp/with-fresh-collected
     [(sp/collect-one :selection)
      (merge-similar* keep-system-flag?)])))

(def expand-selection*
  ;; TODO this path does not work for all permutations of it's args.
  ;; It does only what I need it to do even though it seems like it
  ;; does more. This needs to be addressed at some point.
  (selectionnav
   [{:keys [shift-start-index new-start-index
            shift-start-offset new-start-offset
            shift-end-index new-end-index
            shift-end-offset new-end-offset
            caret?]
     :or {shift-start-index 0
          shift-start-offset 0
          shift-end-index 0
          shift-end-offset 0}
     :as shifts}]
   (transform*
    [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
    (let [current-word (sp/select-one [:script (sp/nthpath end-index) :word] structure)
          word-count #(sp/select-one [:script (sp/nthpath %) :word (sp/view count)] structure)
          current-word-count (count current-word)
          current-next-count (- current-word-count end-offset)]
      (cond

        (and (< current-next-count shift-end-offset)
             caret?)
        (sp/transform
         [:selection
          (sp/terminal-val
           {:start-index (inc start-index)
            :start-offset 1
            :end-index (inc end-index)
            :end-offset 1})]
         next-fn
         structure)

        (< current-next-count shift-end-offset)
        (sp/transform
         [(sp/multi-path
           [:selection
            (sp/terminal-val
             {:start-index (inc start-index)
              :start-offset 0
              :end-index (inc end-index)
              :end-offset 0})]
           (sp/with-fresh-collected
             [(sp/collect-one :selection)
              (expand-selection*
               ;; TODO this is only used in a specific places and is
               ;; not as general as it could/should be
               {:shift-end-offset (- shift-end-offset current-next-count)})]))]
         next-fn
         structure)

        :else
        (let [new-start-index (case new-start-index
                                :end-index end-index
                                new-start-index)
              ;; TODO we need to do the `or` here as `select-one` is a
              ;; macro and can't compile the `or`. Not sure if that's
              ;; by design or bug.
              current-start-index (max 0 (or new-start-index
                                             (+ start-index shift-start-index)))
              current-end-index (min (-> structure :script count)
                                     (or new-end-index
                                         (+ end-index shift-end-index)))
              new-start-offset (case new-start-offset
                                 :last (word-count current-start-index)
                                 new-start-offset)
              new-end-offset (case new-end-offset
                               :last (word-count current-end-index)
                               new-end-offset)
              current-start-offset (or new-start-offset
                                       (+ start-offset shift-start-offset))
              current-end-offset (or new-end-offset
                                     (+ end-offset shift-end-offset))
              current-selection {:start-index current-start-index
                                 :start-offset current-start-offset
                                 :end-index current-end-index
                                 :end-offset current-end-offset}
              current-selection (cond-> current-selection
                                  (and (= current-start-offset -1)
                                       (pos? current-start-index))
                                  (assoc :start-offset (-> current-start-index dec word-count dec)
                                         :start-index (dec current-start-index))

                                  (and (= current-end-offset -1)
                                       (pos? current-end-index))
                                  (assoc :end-offset (-> current-end-index dec word-count dec)
                                         :end-index (dec current-end-index)))]
          (sp/transform
           [:selection
            (sp/terminal-val
             current-selection)]
           next-fn
           structure)))))))

(defn expand-selection
  "Changes the selection using either absolute or relative values.
  Takes a map with any combination of keys in the format:
  :(new/shift)-(start/end)-(index/offset)

  The values can be numbers or:
  * :new-start-index can be a keyword :end-index,
  * :new-start-offset / :new-end-offset can be a keyword :last.

  If both absolute and relative values are missing, preserves the current ones.
  Forces the indexes to be in a safe range. Offsets are calculated in terms of
  the newly selected tokens.

  Special case 1: if :shift-end-offset is used and the new offset would exceed
  the current word from the right, the next word is selected instead, selecting
  just the excess number of characters. This can change caret into selection
  (not sure if due to a bug or is it intended) unless a new option :caret? is
  set to true.

  Special case 2: instead of setting the offset to -1, set it to the penultimate
  character of a token on the left."
  [shifts]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (expand-selection* shifts)]))

(def
  ^{:doc "Succeeds if the caret (no range selection) is at the end of a token."}
  end-of-token?
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (select*
       [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
       (if (and (caret? selection)
                (= end-offset (sp/select-one
                               [:script
                                (sp/nthpath end-index)
                                :word
                                (sp/view count)]
                               structure)))
         (next-fn structure)
         sp/NONE)))]))

(defn start-of-token?
  "Is the caret (no range selection) at the start of a token?"
  [{:keys [selection] :as _document}]
  (and (caret? selection)
       (zero? (:start-offset selection))))

(defn if-only
  [start-index end-index types]
  (sp/not-selected?
   [:script
    (sp/srange start-index (inc end-index))
    sp/ALL
    :type
    (fn [value]
      (not (contains? types value)))]))

(def script-start-matcher [])
(def script-end-matcher [])
(def caret-matcher "")
(def end-of-word "")

(defn char-context
  [{:keys [start-index start-offset end-index end-offset] :as selection}]
  [(sp/collect-one :selection)
   :script
   selection-srange
   sp/ALL
   :word
   (sp/multi-path
    (safe-srange (- start-offset 2) start-offset)
    (safe-srange start-offset end-offset)
    (safe-srange end-offset (+ end-offset 2)))
   sp/DISPENSE])

(def char-context?*
  (selectionnav
   [context-matcher]
   (select*
    [_ selection structure next-fn]
    (let [chars (sp/select (char-context selection) structure)]
      (if (m/match? context-matcher chars)
        (next-fn structure)
        sp/NONE)))))

(defn char-context?
  "A predicate taking a vector of three rothko matchers which succeeds if they
  all match, respectively, assuming a caret (no range) selection:
  * a string of 1 or 2 characters before the caret (depending on the caret
    position within the token which dictates how many characters are available),
  * an empty string (because no range is selected),
  * a string of 0, 1 or 2 characters after the caret (depending on the caret
    position within the token and the token length which dictate how many
    characters are available).
  If a range is selected (not just a caret), the strings to match would extend
  accordingly.
  In an extreme case, when a selection range spans multiple tokens, a multiple
  of 3 matchers would be needed to make a match."
  [context-matcher]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (char-context?* context-matcher)]))

(def context?*
  (selectionnav
   [context-matcher]
   (select*
    [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
    ;; TODO should collect this once at the top of the action, that
    ;; would prevent it being run on each check
    (let [context (sp/select-one
                   [(sp/collect-one :selection)
                    :script
                    (selection-srange-with-context 2 2)
                    sp/DISPENSE]
                   structure)
          satisfied? true]
      (if (m/match? context-matcher context)
        (next-fn structure)
        sp/NONE)))))

(defn debug-context
  [document]
  (#?(:cljs cljs.pprint/pprint
      :clj clojure.pprint/pprint)
   (sp/select-one
    [(sp/collect-one :selection)
     :script
     (selection-srange-with-context 2 2)
     sp/DISPENSE]
    document))
  document)

(defn log-or-print
  "Without debugging tools, console.log cannot display readable data structures."
  [x]
  #?(:cljs (if js/goog.DEBUG (js/console.log x) (cljs.pprint/pprint x))))

(def
  ^{:doc "A debugging action that prints out the selected tokens."}
  log-selected-tokens
  [(sp/terminal
    (fn [{{:keys [start-index end-index]} :selection :keys [script] :as structure}]
      (log-or-print (->> (subvec script start-index (inc end-index))
                         (mapv #(dissoc % :score))))
      structure))])

(defn and-paths-debug
  "Like `and-paths`, but also logs the `message` if all `clauses` are fulfilled."
  [message & clauses]
  (assert (or (string? message) (number? message))
          "The first argument to and-paths-debug should be a log message (string or number).")
  (conj
   (vec clauses)
   (fn [& [{:keys [selection]} :as args]]
     (let [ctx (cond
                 (nil? selection) args
                 (caret? selection) (list* 'caret ((juxt :start-index :start-offset) selection))
                 :else selection)]
       (println (str ">>> " message " \033[37m" (pr-str ctx) "\033[0m")))
     true)))

(defn context?
  "A predicate taking a vector of three rothko matchers which succeeds if they
  all match, respectively:
  * two tokens preceding the current editor selection,
  * token(s) currently selected in the editor,
  * two tokens following the current editor selection."
  [options]
  (sp/with-fresh-collected
    [(sp/collect-one :selection) ; TODO: Either `context?*` or `selection-srange-with-context` should use a standard `nav`, then collecting this twice wouldn't be needed.
     (context?* options)]))

(defn empty-document?
  [{:keys [script] :as document}]
  (empty? script))

(defn end-of-script?
  [{:keys [script] {:keys [start-index start-offset]} :selection :as document}]
  (and (= start-offset
          (-> script last :word count))
       (= start-index
          (dec (count script)))))

(defn start-of-script?
  [{{:keys [start-index start-offset end-index end-offset]} :selection :as document}]
  (= 0 start-index start-offset end-index end-offset))

(defn if-caret?
  [then else]
  (sp/if-path CARET? then else))

;; These two functions are to be used in action definitions as in the
;; context of specter's `if-path` and `cond-path` it's not
;; immediateley obvious that `multi-path` is semantically an 'or' and
;; `[]` is semantically an 'and'
(defn or-paths
  [& clauses]
  (apply sp/multi-path clauses))

(defn and-paths
  [& clauses]
  clauses)

(def entire-word-selected?
  (char-context? ["" m/?any ""]))

(defn make-mark-pred
  [afn k]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     :script
     selection-srange
     (fn [token-range]
       (every? (fn [token]
                 (or (and (= :space (:type token))
                          (not (= 1 (count token-range))))
                     (afn (contains? token k))))
               token-range))]))

(def not-edited? (make-mark-pred not :edited?))

(def edited? (make-mark-pred identity :edited?))

(defn merge-tokens
  "Takes a sequence of tokens and returns a single map containing just
  :type and/or :marks with corresponding values last seen in the input.
  Optionally can also keep the :system? flag."
  ([token-range] (merge-tokens token-range false))
  ([token-range keep-system-flag?]
   (let [keyseq (cond-> [:type :marks]
                  keep-system-flag? (conj :system?))]
     (transduce
      (map #(select-keys % keyseq))
      merge
      {}
      token-range))))

(defn edit-tokens-from-merged-token
  "Takes a token returned from merge-tokens and a selected string.
  Splits the string and returns a collection of :edit and :space(-edit) tokens."
  [merged-token word space-edits?]
  (->> word
       (map str)
       (partition-by script-spec/blank?)
       (mapcat #(if (and space-edits?
                         (script-spec/blank? (first %)))
                  % ; Don't join multiple space-edits. Keep them separate.
                  [(str/join %)]))
       (map #(let [blank? (script-spec/blank? %)]
               (assoc
                merged-token
                :word %
                :type (cond
                        (not blank?) :edit
                        space-edits? :space-edit
                        :else :space))))))

(def selection-insert*
  (selectionnav
   [chars keep-system-flag? token-type]
   (transform*
    [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
    (sp/transform
     [(sp/collect-one surrounds)
      (sp/multi-path
       [:script
        (sp/srange start-index (inc end-index))
        (sp/terminal
         (fn [words token-range]
           (let [token (-> (merge-tokens token-range keep-system-flag?)
                           (medley/assoc-some :type token-type))
                 word (apply str (interpose chars words))]
             [(assoc token :word word)])))]
       [:selection
        (sp/terminal-val
         {:start-index start-index
          :start-offset (+ start-offset (count chars))
          :end-index start-index
          :end-offset (+ start-offset (count chars))})])]
     next-fn
     structure))))

(defn selection-insert
  ([chars keep-system-flag?] (selection-insert chars keep-system-flag? nil))
  ([chars keep-system-flag? token-type]
   (sp/with-fresh-collected
     [(sp/collect-one :selection)
      (selection-insert* chars keep-system-flag? token-type)])))

(def split
  ;; TODO use this for :space action. I.e., :space is a split then space token insert
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (transform*
       [_ {:keys [start-index start-offset end-index end-offset] :as selection} structure next-fn]
       (sp/transform
        [(sp/collect-one surrounds)
         (sp/multi-path
          [:script
           (sp/srange start-index (inc end-index))
           (sp/terminal
            (fn [[start-to-offset offset-to-last] token-range]
              (let [token (merge-tokens token-range)]
                [(assoc token :word start-to-offset)
                 (assoc token :word offset-to-last)])))])]
        next-fn
        structure)))]))

(def move-selection-one-char-to-right
  (expand-selection
   {:shift-start-index 1
    :shift-end-index 1
    :new-start-offset 0
    :new-end-offset 0}))

(def move-selection-one-char-to-left
  (expand-selection
   {:shift-start-index -1
    :shift-end-index -1
    :new-start-offset 0
    :new-end-offset 0}))

(defn remove-all*
  [{:keys [start-index end-index start-offset]} token-path key-path]
  [(sp/collect
    :script
    (sp/srange start-index (inc end-index))
    sp/ALL
    token-path)
   (sp/multi-path
    [:script
     (sp/srange start-index (inc end-index))
     sp/ALL
     (sp/multi-path
      [key-path
       (sp/terminal-val sp/NONE)]
      [(sp/selected? token-path)
       (sp/terminal-val sp/NONE)])]
    [(sp/collect-one
      :script)
     :selection
     (sp/terminal
      (fn [removed-tokens script selection]
        (if (empty? removed-tokens)
          selection
          (let [token-length-at #(if (< % (count script))
                                   (-> script (nth %) :word count)
                                   0)
                new-start-offset (min (token-length-at start-index) start-offset)
                new-end-index' (- end-index (count removed-tokens))
                new-end-index (max 0 new-end-index')
                new-end-offset (if (neg? new-end-index')
                                 new-start-offset
                                 (token-length-at new-end-index))
                start-after-end? (> start-index new-end-index)]
            (cond-> (script/selection start-index new-start-offset new-end-index new-end-offset)
              start-after-end? (assoc :start-index new-end-index)
              start-after-end? (assoc :start-offset new-end-offset))))))])])

(defn remove-all
  "Removes all tokens matching `token-path` from the selection and shrinks
  it accordingly. Also removes `key-path` key from the selected tokens."
  [token-path key-path]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (selectionnav
      []
      (transform*
       [_ selection structure next-fn]
       (sp/transform
        (remove-all* selection token-path key-path)
        next-fn
        structure)))]))

(defn effect
  "Runs a side-effecting `f` passing to it the document with the selection normalised.
  Ignores `f`'s return value."
  [f]
  [(sp/terminal #(doto % f))])
